import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Trophy,
  AlertCircle,
  Target,
  TrendingUp,
  Lightbulb,
  Quote,
  ArrowRight,
  Sparkles,
  Clock,
  Dumbbell,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const VERDICT_BAR = {
  PASS: { Icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-900', label: 'Pass' },
  FAIL: { Icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-900', label: 'Fail' },
  NOT_APPLICABLE: { Icon: MinusCircle, color: 'text-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/40', border: 'border-slate-200 dark:border-slate-800', label: 'N/A' },
};

function normaliseVerdict(value) {
  if (!value || typeof value !== 'string') return 'NOT_APPLICABLE';
  const v = value.trim().toUpperCase();
  if (v.startsWith('PASS')) return 'PASS';
  if (v.startsWith('FAIL')) return 'FAIL';
  if (v.includes('N/A') || v.includes('NOT_APPLICABLE') || v.includes('NOT APPLICABLE')) return 'NOT_APPLICABLE';
  return value; // free text — render as-is
}

// Strip a single pair of surrounding quotes (so we don't render `""Hello""`)
function trimQuotes(s) {
  if (!s || typeof s !== 'string') return s;
  let out = s.trim();
  // Match matching pairs of " or ' or smart quotes around the entire string
  const pairs = [['"', '"'], ["'", "'"], ['\u201C', '\u201D'], ['\u2018', '\u2019']];
  for (const [open, close] of pairs) {
    if (out.length >= 2 && out.startsWith(open) && out.endsWith(close)) {
      out = out.slice(1, -1).trim();
    }
  }
  return out;
}

function ComplianceRow({ label, value }) {
  if (!value) return null;
  const verdict = normaliseVerdict(value);
  const meta = VERDICT_BAR[verdict];
  if (!meta) {
    // Free-text verdict
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
        <div className="w-32 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide pt-0.5">
          {label}
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{value}</p>
      </div>
    );
  }
  const { Icon, color, bg, border, label: badgeLabel } = meta;
  // The raw value can contain "PASS — recruiter said \"...\"" → preserve everything after the verdict word
  const detail = (value || '').replace(/^(PASS|FAIL|N\/A|NOT[_ ]APPLICABLE)[\s—:-]*/i, '').trim();
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="w-32 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide pt-1">
        {label}
      </div>
      <Badge
        data-testid={`elr-compliance-${label.toLowerCase().replace(/\s+/g, '-')}`}
        variant="outline"
        className={`shrink-0 ${bg} ${border} ${color} gap-1 font-semibold`}
      >
        <Icon className="h-3 w-3" />
        {badgeLabel}
      </Badge>
      {detail && (
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed flex-1">
          {detail}
        </p>
      )}
    </div>
  );
}

/**
 * Expert Learning Report — multi-section coaching feedback that turns
 * each call into a learning experience for the agent.
 *
 * Sections rendered (only if data is present — backwards-compatible with
 * older calls):
 *   1. Verdict + headline score summary (hero)
 *   2. Compliance audit summary
 *   3. Top 3 wins (with timestamps + quotes + why-it-worked)
 *   4. Top 3 misses (with "try this instead" recommended phrasing)
 *   5. Practice drills (hands-on 5-min exercises with success criteria)
 *   6. Next-call focus (1-3 specific behaviours to practice)
 *   7. How top performers handle this (industry benchmark)
 *   8. Reflection question (open prompt for self-assessment)
 */
export default function ExpertLearningReport({ report }) {
  const [openDrillIdx, setOpenDrillIdx] = useState(null);

  if (!report || (typeof report === 'object' && Object.keys(report).length === 0)) {
    return null;
  }

  const {
    one_line_verdict,
    headline_score_summary,
    top_3_wins = [],
    top_3_misses = [],
    next_call_focus = [],
    how_top_performers_handle_this,
    reflection_question,
    practice_drills = [],
    compliance_audit_summary,
  } = report;

  const hasCompliance = compliance_audit_summary && Object.values(compliance_audit_summary).some(Boolean);

  return (
    <div data-testid="expert-learning-report" className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b border-slate-200 dark:border-slate-800">
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100">
            Expert Learning Report
          </h3>
          <p className="text-xs text-slate-500">
            Coaching-first feedback. Read each section, then run the drills before your next call.
          </p>
        </div>
      </div>

      {/* 1. Verdict + headline summary */}
      {(one_line_verdict || headline_score_summary) && (
        <Card data-testid="elr-verdict" className="border-2 border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/40 dark:from-violet-950/20 dark:to-fuchsia-950/10">
          <CardContent className="p-5 space-y-3">
            {one_line_verdict && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-violet-700 dark:text-violet-300 font-semibold">
                  Verdict
                </p>
                <p className="text-lg font-medium text-slate-900 dark:text-slate-100 leading-snug">
                  {one_line_verdict}
                </p>
              </>
            )}
            {headline_score_summary && (
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed pt-1 border-t border-violet-100 dark:border-violet-900/50">
                {headline_score_summary}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* 2. Compliance audit summary */}
      {hasCompliance && (
        <Card data-testid="elr-compliance" className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-slate-700 dark:text-slate-300" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                Compliance Audit
              </h4>
            </div>
            <div className="space-y-0">
              <ComplianceRow label="Branding" value={compliance_audit_summary?.branding_check} />
              <ComplianceRow label="Disclaimer" value={compliance_audit_summary?.disclaimer_check} />
              <ComplianceRow label="Work Auth" value={compliance_audit_summary?.work_authorization_check} />
              <ComplianceRow label="Probing" value={compliance_audit_summary?.probing_check} />
              <ComplianceRow label="RTR" value={compliance_audit_summary?.rtr_check} />
            </div>
            {compliance_audit_summary?.fatal_violations_summary && (
              <div className="mt-3 p-3 rounded-lg bg-red-50/60 dark:bg-red-950/20 border border-red-100 dark:border-red-900">
                <p className="text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300 font-semibold mb-1">
                  Fatal Violations
                </p>
                <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">
                  {compliance_audit_summary.fatal_violations_summary}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3. Top 3 Wins */}
      {top_3_wins.length > 0 && (
        <section data-testid="elr-wins">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-emerald-600" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-100">Top wins — keep doing this</h4>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              {top_3_wins.length}
            </Badge>
          </div>
          <div className="space-y-2.5">
            {top_3_wins.map((win, i) => (
              <Card key={i} className="border-l-4 border-l-emerald-500" data-testid={`elr-win-${i}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{win.what}</p>
                    {win.timestamp && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        <Clock className="h-3 w-3 mr-1" />
                        {win.timestamp}
                      </Badge>
                    )}
                  </div>
                  {win.quote && (
                    <div className="flex gap-2 p-2.5 bg-emerald-50/60 dark:bg-emerald-950/20 rounded border border-emerald-100 dark:border-emerald-900">
                      <Quote className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-slate-700 dark:text-slate-300 italic">"{trimQuotes(win.quote)}"</p>
                    </div>
                  )}
                  {win.why_it_worked && (
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      <span className="font-medium text-emerald-700 dark:text-emerald-400">Why it worked: </span>
                      {win.why_it_worked}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* 4. Top 3 Misses */}
      {top_3_misses.length > 0 && (
        <section data-testid="elr-misses">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-100">
              Top misses — try this instead
            </h4>
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              {top_3_misses.length}
            </Badge>
          </div>
          <div className="space-y-2.5">
            {top_3_misses.map((miss, i) => (
              <Card key={i} className="border-l-4 border-l-amber-500" data-testid={`elr-miss-${i}`}>
                <CardContent className="p-4 space-y-2.5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{miss.what}</p>
                    {miss.timestamp && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        <Clock className="h-3 w-3 mr-1" />
                        {miss.timestamp}
                      </Badge>
                    )}
                  </div>
                  {miss.what_was_said && (
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                        What you said
                      </p>
                      <div className="flex gap-2 p-2.5 bg-red-50/60 dark:bg-red-950/20 rounded border border-red-100 dark:border-red-900">
                        <Quote className="h-3.5 w-3.5 text-red-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-slate-700 dark:text-slate-300 italic">"{trimQuotes(miss.what_was_said)}"</p>
                      </div>
                    </div>
                  )}
                  {miss.try_this_instead && (
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <ArrowRight className="h-3 w-3" /> Try this instead
                      </p>
                      <div className="flex gap-2 p-2.5 bg-emerald-50/60 dark:bg-emerald-950/20 rounded border border-emerald-200 dark:border-emerald-800">
                        <p className="text-xs text-slate-800 dark:text-slate-200 font-medium">"{trimQuotes(miss.try_this_instead)}"</p>
                      </div>
                    </div>
                  )}
                  {miss.impact && (
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      <span className="font-medium text-amber-700 dark:text-amber-400">Impact: </span>
                      {miss.impact}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* 5. Practice drills */}
      {practice_drills.length > 0 && (
        <section data-testid="elr-drills">
          <div className="flex items-center gap-2 mb-3">
            <Dumbbell className="h-4 w-4 text-rose-600" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-100">
              Practice drills — do these today
            </h4>
            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
              {practice_drills.length}
            </Badge>
          </div>
          <div className="space-y-2.5">
            {practice_drills.map((drill, i) => {
              const isOpen = openDrillIdx === i;
              return (
                <Card
                  key={i}
                  className="border-l-4 border-l-rose-500"
                  data-testid={`elr-drill-${i}`}
                >
                  <CardContent className="p-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => setOpenDrillIdx(isOpen ? null : i)}
                      className="w-full flex items-start justify-between gap-3 text-left"
                      data-testid={`elr-drill-toggle-${i}`}
                    >
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                            {drill.title || `Drill ${i + 1}`}
                          </p>
                          {drill.skill_area && (
                            <Badge variant="outline" className="text-[10px] bg-rose-50/60 border-rose-200 text-rose-700">
                              {drill.skill_area}
                            </Badge>
                          )}
                          {drill.duration_minutes && (
                            <Badge variant="outline" className="text-[10px] gap-1">
                              <Clock className="h-2.5 w-2.5" />
                              {drill.duration_minutes} min
                            </Badge>
                          )}
                        </div>
                        {drill.linked_to && !isOpen && (
                          <p className="text-xs text-slate-500 italic line-clamp-1">
                            Fixes: {drill.linked_to}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 pt-1">
                        {isOpen ? (
                          <ChevronUp className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                        {drill.instructions && (
                          <div className="space-y-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                              How to run it
                            </p>
                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                              {drill.instructions}
                            </p>
                          </div>
                        )}
                        {drill.success_criteria && (
                          <div className="p-2.5 rounded bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900">
                            <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold mb-0.5">
                              Success criteria
                            </p>
                            <p className="text-xs text-slate-700 dark:text-slate-300">{drill.success_criteria}</p>
                          </div>
                        )}
                        {drill.linked_to && (
                          <p className="text-xs text-slate-500 italic">
                            <span className="font-medium not-italic">Fixes: </span>
                            {drill.linked_to}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* 6. Next call focus */}
      {next_call_focus.length > 0 && (
        <Card data-testid="elr-next-call" className="border-2 border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-950/10">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-blue-600" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                Focus for your next call
              </h4>
            </div>
            <ul className="space-y-2">
              {next_call_focus.map((focus, i) => (
                <li
                  key={i}
                  data-testid={`elr-focus-${i}`}
                  className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300"
                >
                  <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <span>{focus}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* 7. Top-performer benchmark */}
      {how_top_performers_handle_this && (
        <Card data-testid="elr-benchmark" className="border-2 border-indigo-200 dark:border-indigo-900">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-indigo-600" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                How top performers handle this
              </h4>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {how_top_performers_handle_this}
            </p>
          </CardContent>
        </Card>
      )}

      {/* 8. Reflection question */}
      {reflection_question && (
        <Card data-testid="elr-reflection" className="border-2 border-fuchsia-200 dark:border-fuchsia-900 bg-gradient-to-br from-fuchsia-50/60 to-pink-50/40 dark:from-fuchsia-950/20 dark:to-pink-950/10">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-fuchsia-600" />
              <p className="text-[10px] uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 font-semibold">
                Reflection
              </p>
            </div>
            <p className="text-base font-medium text-slate-900 dark:text-slate-100 leading-relaxed italic">
              "{trimQuotes(reflection_question)}"
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Take 60 seconds before your next call to think about this.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Named export kept for any importers that prefer `{ Button as _ } = ...` style
export { ExpertLearningReport };
